/**
 * GeneralLedgerPage.js  –  Double-Entry Accounting Engine
 * Chart of Accounts · Journal Entries · Trial Balance · P&L · Balance Sheet
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';
import { getBrandingSlug } from '../utils/formatters';

// Read live branding from localStorage (same source as print.js)
function getLedgerBranding() {
  try {
    const raw = localStorage.getItem('grand_theme_v4') || localStorage.getItem('grand_theme_v2') || localStorage.getItem('grand_theme_v1');
    if (raw) { const t = JSON.parse(raw); return t.hotelName || 'Grand Memories Resort & Spa'; }
  } catch {}
  return 'Grand Memories Resort & Spa';
}

const gold = '#C9A84C';
const green = '#2ecc71';
const red   = '#e74c3c';
const blue = '#3498db';

const ACC_TYPES = ['asset','liability','equity','revenue','expense'];
const ENTRY_TYPES = ['manual','sales','purchase','payroll','expense','payment','depreciation','adjustment'];

const Row = ({ label, value, accent, indent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0',
    paddingLeft: indent ? 20 : 0, borderBottom: '1px solid #1e1e1e' }}>
    <span style={{ color: accent ? 'var(--text)' : 'var(--textMuted)', fontSize: 13,
      fontWeight: accent ? 700 : 400 }}>{label}</span>
    <span style={{ fontWeight: accent ? 700 : 500, color: accent ? gold : 'var(--text)',
      fontSize: 13 }}>{typeof value === 'number' ? fmtKES(value) : value}</span>
  </div>
);

const SectionHead = ({ title }) => (
  <div style={{ fontWeight: 700, color: gold, fontSize: 13, margin: '18px 0 8px',
    paddingBottom: 6, borderBottom: `2px solid ${gold}44` }}>{title}</div>
);

const statusColor = { draft: '#888', posted: green, reversed: red };

export default function GeneralLedgerPage({ notify, userRole }) {
  const [tab, setTab]           = useState('journal');
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [tb, setTb]             = useState(null);
  const [pl, setPL]             = useState(null);
  const [bs, setBS]             = useState(null);
  const [, setLoading]   = useState(false);

  // Journal form state
  const [showJEForm, setShowJEForm] = useState(false);
  const [jeForm, setJeForm] = useState({ entry_date: new Date().toISOString().split('T')[0], description: '', entry_type: 'manual', notes: '' });
  const [jeLines, setJeLines] = useState([{ account: '', debit: '', credit: '', description: '' }]);
  const [saving, setSaving]   = useState(false);

  // COA form
  const [showCOAForm, setShowCOAForm] = useState(false);
  const [coaForm, setCoaForm] = useState({ code: '', name: '', account_type: 'asset', sub_type: '', description: '' });

  // Filters
  const [jeFilter, setJeFilter] = useState({ status: '', entry_type: '', date_from: '', date_to: '' });
  const [plDates, setPLDates]   = useState({ date_from: '', date_to: '' });

  const canPost = ['admin', 'accountant'].includes(userRole);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, jeRes] = await Promise.all([
        api.getCOA(),
        api.getJournals(),
      ]);
      setAccounts(accRes.data.results || accRes.data || []);
      setJournals(jeRes.data.results  || jeRes.data  || []);
    } catch { notify?.('Failed to load accounting data', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const loadTrialBalance = async () => {
    try { const r = await api.getTrialBalance(); setTb(r.data); }
    catch { notify?.('Failed to load trial balance', 'error'); }
  };

  const loadPL = async () => {
    const p = `?date_from=${plDates.date_from}&date_to=${plDates.date_to}`;
    try { const r = await api.getProfitAndLoss(p); setPL(r.data); }
    catch { notify?.('Failed to load P&L', 'error'); }
  };

  const loadBS = async () => {
    try { const r = await api.getBalanceSheet(); setBS(r.data); }
    catch { notify?.('Failed to load Balance Sheet', 'error'); }
  };

  useEffect(() => { if (tab === 'tb') loadTrialBalance(); }, [tab]);
  useEffect(() => { if (tab === 'bs') loadBS(); }, [tab]);

  const addJELine = () => setJeLines(prev => [...prev, { account: '', debit: '', credit: '', description: '' }]);
  const removeJELine = (i) => setJeLines(prev => prev.filter((_, idx) => idx !== i));
  const updateLine = (i, field, val) => setJeLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));

  const totalDebit  = jeLines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
  const totalCredit = jeLines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced  = Math.abs(totalDebit - totalCredit) < 0.01;

  const saveJournal = async () => {
    if (!jeForm.description) { notify?.('Description required', 'error'); return; }
    if (jeLines.length < 2)  { notify?.('At least 2 lines required', 'error'); return; }
    if (!isBalanced)         { notify?.(`Entry not balanced: Dr ${fmtKES(totalDebit)} ≠ Cr ${fmtKES(totalCredit)}`, 'error'); return; }
    const validLines = jeLines.filter(l => l.account);
    if (validLines.length < 2) { notify?.('At least 2 lines with accounts required', 'error'); return; }
    setSaving(true);
    try {
      // Step 1: Create the journal header
      const jeRes = await api.createJournal(jeForm);
      const jeId = jeRes.data.id;
      // Step 2: POST each line individually to the journal lines endpoint
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('gm_authtoken');
      for (const line of validLines) {
        await fetch(`${baseUrl}/api/accounts/journal-lines/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
          body: JSON.stringify({
            journal: jeId,
            account: line.account,
            debit: parseFloat(line.debit) || 0,
            credit: parseFloat(line.credit) || 0,
            description: line.description || '',
          }),
        });
      }
      notify?.('Journal entry saved as draft');
      setShowJEForm(false);
      setJeForm({ entry_date: new Date().toISOString().split('T')[0], description: '', entry_type: 'manual', notes: '' });
      setJeLines([{ account: '', debit: '', credit: '', description: '' }]);
      load();
    } catch (e) { notify?.('Failed to save journal entry', 'error'); }
    finally { setSaving(false); }
  };

  // ── Export helpers ────────────────────────────────────────────────────────
  const exportCSV = (filename, headers, rows) => {
    const lines = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = filename; a.click();
  };

  const exportTBCSV = () => {
    if (!tb) return;
    exportCSV(`${getBrandingSlug()}_TrialBalance.csv`, ['Code','Account Name','Type','Debit','Credit'],
      tb.rows.map(r => [r.code, r.name, r.account_type, r.debit, r.credit]));
  };

  const exportPLCSV = () => {
    if (!pl) return;
    const rows = [
      ['REVENUE','',''],
      ...pl.revenues.map(r => [r.code, r.name, r.amount]),
      ['TOTAL REVENUE','', pl.total_revenue],
      ['EXPENSES','',''],
      ...pl.expenses.map(r => [r.code, r.name, r.amount]),
      ['TOTAL EXPENSES','', pl.total_expenses],
      ['NET PROFIT / LOSS','', pl.net_profit],
    ];
    exportCSV(`${getBrandingSlug()}_ProfitAndLoss.csv`, ['Code','Account','Amount (KES)'], rows);
  };

  const exportBSCSV = () => {
    if (!bs) return;
    const rows = [
      ['ASSETS','',''],
      ...bs.assets.map(a => [a.code, a.name, a.balance]),
      ['TOTAL ASSETS','', bs.totals.assets],
      ['LIABILITIES','',''],
      ...bs.liabilities.map(a => [a.code, a.name, a.balance]),
      ['TOTAL LIABILITIES','', bs.totals.liabilities],
      ['EQUITY','',''],
      ...bs.equity.map(a => [a.code, a.name, a.balance]),
      ['TOTAL EQUITY','', bs.totals.equity],
    ];
    exportCSV(`${getBrandingSlug()}_BalanceSheet.csv`, ['Code','Account','Balance (KES)'], rows);
  };

  // ── Shared print shell ────────────────────────────────────────────────────
  const printDoc = (title, bodyHtml) => {
    const hotelName = getLedgerBranding();
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title} — ${hotelName}</title>
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Source+Sans+3:wght@300;400;600;700&display=swap" rel="stylesheet">
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Source Sans 3',sans-serif;font-size:12px;color:#1a1a2e;background:#f0f2f5;print-color-adjust:exact;-webkit-print-color-adjust:exact}
      /* ── Page header ── */
      .page-header{background:#1a1a2e;color:#fff;padding:20px 32px 16px;display:flex;align-items:center;justify-content:space-between;gap:16px}
      .page-header .hotel{font-family:'Playfair Display',serif;font-size:20px;font-weight:700;letter-spacing:.5px}
      .page-header .doc-title{font-size:22px;font-family:'Playfair Display',serif;font-weight:600;text-align:center;flex:1}
      .page-header .meta{text-align:right;font-size:10px;opacity:.75;line-height:1.6}
      /* ── Content wrapper ── */
      .content{padding:24px 32px;max-width:100%}
      /* ── Summary cards ── */
      .kpi-row{display:flex;gap:12px;margin-bottom:20px}
      .kpi{flex:1;background:#fff;border-radius:8px;padding:14px 16px;border-left:4px solid #1a1a2e;box-shadow:0 1px 4px rgba(0,0,0,.08)}
      .kpi .label{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.8px;color:#666;margin-bottom:4px}
      .kpi .value{font-size:18px;font-weight:700;color:#1a1a2e;font-family:'Playfair Display',serif}
      .kpi.green{border-left-color:#0f9d58}.kpi.green .value{color:#0f6b3c}
      .kpi.red{border-left-color:#d32f2f}.kpi.red .value{color:#b71c1c}
      .kpi.blue{border-left-color:#1565c0}.kpi.blue .value{color:#1565c0}
      .kpi.gold{border-left-color:#b8860b}.kpi.gold .value{color:#7a5a00}
      /* ── Section cards ── */
      .card{background:#fff;border-radius:8px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 4px rgba(0,0,0,.08)}
      .card-header{background:#1a1a2e;color:#fff;padding:10px 16px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px}
      .card-header.accent{background:#2c3e6b}
      .card-header.light{background:#e8ecf4;color:#1a1a2e}
      /* ── Tables ── */
      table{width:100%;border-collapse:collapse}
      thead tr{background:#f7f8fc}
      th{padding:9px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#555;border-bottom:2px solid #e0e4f0}
      th.num,td.num{text-align:right}
      td{padding:8px 14px;font-size:12px;border-bottom:1px solid #f0f2f8;color:#2d2d44}
      tr:hover td{background:#f7f8fc}
      tr.section-row td{background:#e8ecf4;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.6px;color:#1a1a2e;padding:7px 14px}
      tr.subtotal td{background:#f0f4ff;font-weight:600;font-size:12px;color:#1a1a2e}
      tr.total-row td{background:#1a1a2e;color:#fff;font-weight:700;font-size:13px;padding:10px 14px}
      tr.total-row td.num{color:#fff}
      td.code{font-family:monospace;font-size:11px;color:#7c5a00;background:#fffbf0;font-weight:600;border-right:1px solid #f0e8d0}
      /* ── Balance status ── */
      .balance-status{text-align:center;padding:12px;font-weight:700;font-size:13px;border-radius:0 0 8px 8px}
      .balance-status.ok{background:#e8f5e9;color:#2e7d32}
      .balance-status.err{background:#ffebee;color:#c62828}
      /* ── Two-col layout ── */
      .two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px}
      /* ── Footer ── */
      .page-footer{margin-top:24px;padding:12px 32px;background:#fff;border-top:2px solid #e0e4f0;display:flex;justify-content:space-between;font-size:10px;color:#888}
      @media print{
        body{background:#fff}
        .page-header{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .card-header{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        tr.total-row td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        tr.section-row td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .kpi{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      }
    </style></head>
    <body>
      <div class="page-header">
        <div class="hotel">${hotelName}</div>
        <div class="doc-title">${title}</div>
        <div class="meta">Generated: ${new Date().toLocaleString()}<br>Confidential</div>
      </div>
      <div class="content">${bodyHtml}</div>
      <div class="page-footer">
        <span>${hotelName}</span>
        <span>${title} · ${new Date().toLocaleDateString('en-KE',{year:'numeric',month:'long',day:'numeric'})}</span>
        <span>Page 1</span>
      </div>
    </body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 800);
  };

  const printTB = () => {
    if (!tb) return;
    const bal = Math.abs(tb.total_debit - tb.total_credit) < 0.01;
    const rows = tb.rows.map(r => `
      <tr>
        <td class="code">${r.code}</td>
        <td>${r.name}</td>
        <td><span style="background:#eef2ff;color:#3c4db0;padding:2px 8px;border-radius:12px;font-size:10px;font-weight:600">${r.account_type}</span></td>
        <td class="num">${r.debit > 0 ? fmtKES(r.debit) : '<span style="color:#ccc">—</span>'}</td>
        <td class="num">${r.credit > 0 ? fmtKES(r.credit) : '<span style="color:#ccc">—</span>'}</td>
      </tr>`).join('');
    const kpis = `<div class="kpi-row">
      <div class="kpi blue"><div class="label">Total Debits</div><div class="value">${fmtKES(tb.total_debit)}</div></div>
      <div class="kpi gold"><div class="label">Total Credits</div><div class="value">${fmtKES(tb.total_credit)}</div></div>
      <div class="kpi ${bal?'green':'red'}"><div class="label">Status</div><div class="value">${bal ? '✓ Balanced' : '⚠ Unbalanced'}</div></div>
      <div class="kpi"><div class="label">Accounts</div><div class="value">${tb.rows.length}</div></div>
    </div>`;
    const table = `<div class="card">
      <div class="card-header">Account Balances</div>
      <table>
        <thead><tr><th>Code</th><th>Account Name</th><th>Type</th><th class="num">Debit (KES)</th><th class="num">Credit (KES)</th></tr></thead>
        <tbody>${rows}
          <tr class="total-row"><td colspan="3">TOTALS</td><td class="num">${fmtKES(tb.total_debit)}</td><td class="num">${fmtKES(tb.total_credit)}</td></tr>
        </tbody>
      </table>
      <div class="balance-status ${bal?'ok':'err'}">${bal ? '✓ Ledger is in balance — Debits equal Credits' : '⚠ Ledger is out of balance — please review entries'}</div>
    </div>`;
    printDoc('Trial Balance', kpis + table);
  };

  const printPL = () => {
    if (!pl) return;
    const revRows = pl.revenues.map(r => `<tr><td class="code">${r.code}</td><td>${r.name}</td><td class="num">${fmtKES(r.amount)}</td></tr>`).join('');
    const expRows = pl.expenses.map(r => `<tr><td class="code">${r.code}</td><td>${r.name}</td><td class="num">${fmtKES(r.amount)}</td></tr>`).join('');
    const margin = pl.total_revenue > 0 ? ((pl.net_profit / pl.total_revenue) * 100).toFixed(1) : '0.0';
    const kpis = `<div class="kpi-row">
      <div class="kpi blue"><div class="label">Total Revenue</div><div class="value">${fmtKES(pl.total_revenue)}</div></div>
      <div class="kpi red"><div class="label">Total Expenses</div><div class="value">${fmtKES(pl.total_expenses)}</div></div>
      <div class="kpi ${pl.net_profit>=0?'green':'red'}"><div class="label">Net Profit / Loss</div><div class="value">${fmtKES(pl.net_profit)}</div></div>
      <div class="kpi gold"><div class="label">Net Margin</div><div class="value">${margin}%</div></div>
    </div>`;
    const table = `<div class="card">
      <div class="card-header">Income & Expenditure</div>
      <table>
        <thead><tr><th>Code</th><th>Account</th><th class="num">Amount (KES)</th></tr></thead>
        <tbody>
          <tr class="section-row"><td colspan="3">▸ REVENUE</td></tr>${revRows}
          <tr class="subtotal"><td colspan="2" style="padding-left:28px">Total Revenue</td><td class="num">${fmtKES(pl.total_revenue)}</td></tr>
          <tr class="section-row"><td colspan="3">▸ EXPENSES</td></tr>${expRows}
          <tr class="subtotal"><td colspan="2" style="padding-left:28px">Total Expenses</td><td class="num">${fmtKES(pl.total_expenses)}</td></tr>
          <tr class="total-row"><td colspan="2">NET PROFIT / LOSS</td><td class="num" style="color:${pl.net_profit>=0?'#a5d6a7':'#ef9a9a'}">${fmtKES(pl.net_profit)}</td></tr>
        </tbody>
      </table>
    </div>`;
    printDoc('Profit & Loss Statement', kpis + table);
  };

  const printBS = () => {
    if (!bs) return;
    const aRows = bs.assets.map(a => `<tr><td class="code">${a.code}</td><td>${a.name}</td><td class="num">${fmtKES(a.balance)}</td></tr>`).join('');
    const lRows = bs.liabilities.map(a => `<tr><td class="code">${a.code}</td><td>${a.name}</td><td class="num">${fmtKES(a.balance)}</td></tr>`).join('');
    const eRows = bs.equity.map(a => `<tr><td class="code">${a.code}</td><td>${a.name}</td><td class="num">${fmtKES(a.balance)}</td></tr>`).join('');
    const balanced = Math.abs(bs.totals.assets - (bs.totals.liabilities + bs.totals.equity)) < 0.01;
    const kpis = `<div class="kpi-row">
      <div class="kpi blue"><div class="label">Total Assets</div><div class="value">${fmtKES(bs.totals.assets)}</div></div>
      <div class="kpi red"><div class="label">Total Liabilities</div><div class="value">${fmtKES(bs.totals.liabilities)}</div></div>
      <div class="kpi gold"><div class="label">Total Equity</div><div class="value">${fmtKES(bs.totals.equity)}</div></div>
      <div class="kpi ${balanced?'green':'red'}"><div class="label">Balance Check</div><div class="value">${balanced?'✓ Balanced':'⚠ Unbalanced'}</div></div>
    </div>`;
    const tables = `<div class="two-col">
      <div class="card">
        <div class="card-header">Assets</div>
        <table><thead><tr><th>Code</th><th>Account</th><th class="num">Balance (KES)</th></tr></thead>
        <tbody>${aRows}<tr class="total-row"><td colspan="2">Total Assets</td><td class="num">${fmtKES(bs.totals.assets)}</td></tr></tbody></table>
      </div>
      <div>
        <div class="card" style="margin-bottom:16px">
          <div class="card-header accent">Liabilities</div>
          <table><thead><tr><th>Code</th><th>Account</th><th class="num">Balance (KES)</th></tr></thead>
          <tbody>${lRows}<tr class="total-row"><td colspan="2">Total Liabilities</td><td class="num">${fmtKES(bs.totals.liabilities)}</td></tr></tbody></table>
        </div>
        <div class="card">
          <div class="card-header" style="background:#2c5f2e">Equity</div>
          <table><thead><tr><th>Code</th><th>Account</th><th class="num">Balance (KES)</th></tr></thead>
          <tbody>${eRows}<tr class="total-row" style="background:#2c5f2e"><td colspan="2">Total Equity</td><td class="num">${fmtKES(bs.totals.equity)}</td></tr></tbody></table>
        </div>
      </div>
    </div>
    <div class="balance-status ${balanced?'ok':'err'}" style="border-radius:8px;margin-top:0">
      ${balanced ? '✓ Balance Sheet is balanced — Assets = Liabilities + Equity' : '⚠ Balance Sheet does not balance — please review accounts'}
    </div>`;
    printDoc('Balance Sheet', kpis + tables);
  };

  const postEntry = async (id) => {
    try { await api.postJournal(id); notify?.('Entry posted to ledger'); load(); }
    catch (e) { notify?.(e?.response?.data?.error || 'Failed to post', 'error'); }
  };

  const reverseEntry = async (id) => {
    if (!window.confirm('Create a reversing entry?')) return;
    try { await api.reverseJournal(id); notify?.('Reversing entry created'); load(); }
    catch { notify?.('Failed to reverse', 'error'); }
  };

  const saveCOA = async () => {
    if (!coaForm.code || !coaForm.name) { notify?.('Code and name required', 'error'); return; }
    try {
      await api.createAccount(coaForm);
      notify?.('Account created');
      setShowCOAForm(false);
      setCoaForm({ code: '', name: '', account_type: 'asset', sub_type: '', description: '' });
      load();
    } catch (e) { notify?.(e?.response?.data?.code?.[0] || 'Failed to create account', 'error'); }
  };

  const filteredJournals = journals.filter(je => {
    if (jeFilter.status && je.status !== jeFilter.status) return false;
    if (jeFilter.entry_type && je.entry_type !== jeFilter.entry_type) return false;
    if (jeFilter.date_from && je.entry_date < jeFilter.date_from) return false;
    if (jeFilter.date_to   && je.entry_date > jeFilter.date_to)   return false;
    return true;
  });

  const tabs = [
    { id: 'journal', label: '📒 Journal Entries' },
    { id: 'coa',     label: '🗂 Chart of Accounts' },
    { id: 'tb',      label: '⚖️ Trial Balance' },
    { id: 'pl',      label: '📊 P&L Statement' },
    { id: 'bs',      label: '🏦 Balance Sheet' },
  ];

  const ExportBar = ({ onCSV, onPrint }) => (
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={onCSV} style={{ background: '#1e3a2a', color: green, border: `1px solid ${green}44`, borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⬇ CSV</button>
      <button onClick={onPrint} style={{ background: '#1a2a3a', color: '#3498db', border: '1px solid #3498db44', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🖨 Print</button>
    </div>
  );

  return (
    <div style={{ padding: '0 0 40px' }}>
      <PageHeader icon="📒" title="General Ledger" sub="Double-entry accounting · Journal · Financial Statements"
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canPost && tab === 'journal' && (
              <button onClick={() => setShowJEForm(true)} style={{ ...btnPrimary }}>+ New Journal Entry</button>
            )}
            {tab === 'coa' && (
              <button onClick={() => setShowCOAForm(true)} style={{ ...btnPrimary }}>+ Add Account</button>
            )}
            {tab === 'tb' && tb && <ExportBar onCSV={exportTBCSV} onPrint={printTB} />}
            {tab === 'pl' && pl && <ExportBar onCSV={exportPLCSV} onPrint={printPL} />}
            {tab === 'bs' && bs && <ExportBar onCSV={exportBSCSV} onPrint={printBS} />}
          </div>
        }
      />
      <div style={{ padding: '0 20px 12px' }}>
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
      </div>

      {/* ── COA Form Modal ───────────────────────────────────────────── */}
      {showCOAForm && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 460, maxWidth: '95vw' }}>
            <h3 style={{ margin: '0 0 20px', color: gold }}>Add Account</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[['code','Code'],['name','Account Name']].map(([k, l]) => (
                <Field key={k} label={l} required>
                  <input value={coaForm[k]} onChange={e => setCoaForm(p => ({ ...p, [k]: e.target.value }))} style={inputStyle} />
                </Field>
              ))}
              <Field label="Type" required>
                <select value={coaForm.account_type} onChange={e => setCoaForm(p => ({ ...p, account_type: e.target.value }))} style={inputStyle}>
                  {ACC_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </Field>
              <Field label="Sub-type">
                <input placeholder="e.g. current_asset" value={coaForm.sub_type} onChange={e => setCoaForm(p => ({ ...p, sub_type: e.target.value }))} style={inputStyle} />
              </Field>
              <Field label="Description" style={{ gridColumn: '1/-1' }}>
                <input value={coaForm.description} onChange={e => setCoaForm(p => ({ ...p, description: e.target.value }))} style={inputStyle} />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={saveCOA} style={{ ...btnPrimary, flex: 1 }}>Save Account</button>
              <button onClick={() => setShowCOAForm(false)} style={{ ...btnSm, flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Journal Entry Form Modal ─────────────────────────────────── */}
      {showJEForm && (
        <div style={{ position: 'fixed', inset: 0, background: '#000c', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: 20 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 750, maxWidth: '95vw' }}>
            <h3 style={{ margin: '0 0 20px', color: gold }}>New Journal Entry</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <Field label="Date" required>
                <input type="date" value={jeForm.entry_date} onChange={e => setJeForm(p => ({ ...p, entry_date: e.target.value }))} style={inputStyle} />
              </Field>
              <Field label="Type">
                <select value={jeForm.entry_type} onChange={e => setJeForm(p => ({ ...p, entry_type: e.target.value }))} style={inputStyle}>
                  {ENTRY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </Field>
              <Field label="Description" required style={{ gridColumn: '1/-1' }}>
                <input value={jeForm.description} onChange={e => setJeForm(p => ({ ...p, description: e.target.value }))} style={inputStyle} />
              </Field>
            </div>

            {/* Lines table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1a1a1a' }}>
                    {['Account', 'Debit (KES)', 'Credit (KES)', 'Description', ''].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: gold, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jeLines.map((line, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1e1e1e' }}>
                      <td style={{ padding: '6px 4px' }}>
                        <select value={line.account} onChange={e => updateLine(i, 'account', e.target.value)}
                          style={{ ...inputStyle, padding: '5px 8px' }}>
                          <option value="">— Select —</option>
                          {ACC_TYPES.map(type => (
                            <optgroup key={type} label={type.toUpperCase()}>
                              {accounts.filter(a => a.account_type === type && a.is_active).map(a => (
                                <option key={a.id} value={a.id}>{a.code} – {a.name}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <input type="number" min="0" step="0.01" placeholder="0.00"
                          value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)}
                          style={{ ...inputStyle, padding: '5px 8px', width: 110 }} />
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <input type="number" min="0" step="0.01" placeholder="0.00"
                          value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)}
                          style={{ ...inputStyle, padding: '5px 8px', width: 110 }} />
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <input placeholder="Narration…" value={line.description} onChange={e => updateLine(i, 'description', e.target.value)}
                          style={{ ...inputStyle, padding: '5px 8px' }} />
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        {jeLines.length > 2 && <button onClick={() => removeJELine(i)} style={{ background: 'none', border: 'none', color: red, cursor: 'pointer', fontSize: 16 }}>✕</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#1a1a1a' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: gold }}>TOTALS</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: isBalanced ? green : red }}>{fmtKES(totalDebit)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: isBalanced ? green : red }}>{fmtKES(totalCredit)}</td>
                    <td colSpan={2} style={{ padding: '8px 10px', color: isBalanced ? green : red, fontSize: 12 }}>
                      {isBalanced ? '✓ Balanced' : `⚠ Difference: ${fmtKES(Math.abs(totalDebit - totalCredit))}`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <button onClick={addJELine} style={{ ...btnSm, marginTop: 10 }}>+ Add Line</button>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={saveJournal} disabled={saving || !isBalanced}
                style={{ ...btnPrimary, flex: 1, opacity: (!isBalanced || saving) ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save as Draft'}
              </button>
              <button onClick={() => setShowJEForm(false)} style={{ ...btnSm, flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Journal Tab ──────────────────────────────────────────────── */}
      {tab === 'journal' && (
        <div style={{ padding: '0 20px' }}>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            {[['status','Status',[['','All'],['draft','Draft'],['posted','Posted'],['reversed','Reversed']]],
              ['entry_type','Type',[['','All'],...ENTRY_TYPES.map(t => [t, t.charAt(0).toUpperCase()+t.slice(1)])]]
            ].map(([field, label, opts]) => (
              <select key={field} value={jeFilter[field]}
                onChange={e => setJeFilter(p => ({ ...p, [field]: e.target.value }))}
                style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, width: 140 }}>
                {opts.map(([v, l]) => <option key={v} value={v}>{label}: {l}</option>)}
              </select>
            ))}
            <input type="date" value={jeFilter.date_from} onChange={e => setJeFilter(p => ({ ...p, date_from: e.target.value }))}
              style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }} />
            <input type="date" value={jeFilter.date_to} onChange={e => setJeFilter(p => ({ ...p, date_to: e.target.value }))}
              style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }} />
          </div>
          {filteredJournals.length === 0 ? <Empty text="No journal entries yet" /> : (
            <div style={{ display:'grid', gap:10 }}>
              {filteredJournals.map(je => (
                <div key={je.id} style={{
                  background:'var(--surface)', borderRadius:12, overflow:'hidden',
                  border:'1px solid rgba(255,255,255,0.06)',
                  borderLeft:`4px solid ${statusColor[je.status]||gold}`,
                  boxShadow:'0 2px 8px rgba(0,0,0,0.1)',
                }}>
                  {/* Entry header */}
                  <div style={{ padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
                        <span style={{ fontWeight:800, color:gold, fontSize:13, fontFamily:'monospace', letterSpacing:'0.04em' }}>{je.ref}</span>
                        <span style={{ background:statusColor[je.status]+'22', color:statusColor[je.status],
                          borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>{je.status}</span>
                        <span style={{ background:'rgba(255,255,255,0.07)', borderRadius:20, padding:'2px 10px',
                          fontSize:11, color:'var(--textMuted)' }}>{je.entry_type}</span>
                        <span style={{ fontSize:11, color:'var(--textMuted)' }}>📅 {je.entry_date}</span>
                      </div>
                      <div style={{ fontWeight:600, color:'var(--text)', fontSize:14 }}>{je.description}</div>
                      <div style={{ marginTop:4, display:'flex', gap:16, fontSize:12 }}>
                        <span style={{ color:'var(--textMuted)' }}>Dr: <strong style={{ color:blue }}>{fmtKES(je.total_debit)}</strong></span>
                        <span style={{ color:'var(--textMuted)' }}>Cr: <strong style={{ color:gold }}>{fmtKES(je.total_credit)}</strong></span>
                        <span style={{ color: je.is_balanced ? green : red, fontWeight:700, fontSize:11 }}>
                          {je.is_balanced ? '✓ Balanced' : '✗ Unbalanced'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                      {canPost && je.status === 'draft' && (
                        <button onClick={() => postEntry(je.id)}
                          style={{ ...btnSm, background:green+'22', color:green, border:`1px solid ${green}44`, fontSize:11, minWidth:80 }}>✓ Post</button>
                      )}
                      {canPost && je.status === 'posted' && (
                        <button onClick={() => reverseEntry(je.id)}
                          style={{ ...btnSm, background:red+'22', color:red, border:`1px solid ${red}44`, fontSize:11, minWidth:80 }}>↩ Reverse</button>
                      )}
                    </div>
                  </div>
                  {/* Lines table */}
                  {je.lines && je.lines.length > 0 && (
                    <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', overflow:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                          <tr style={{ background:'rgba(255,255,255,0.04)' }}>
                            {['Code','Account','Description','Debit (KES)','Credit (KES)'].map(h => (
                              <th key={h} style={{ padding:'7px 14px', textAlign:h.includes('KES')?'right':'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--textMuted)', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {je.lines.map((line, i) => (
                            <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                              <td style={{ padding:'6px 14px', fontFamily:'monospace', fontSize:11, color:gold }}>{line.account_code}</td>
                              <td style={{ padding:'6px 14px', color:'var(--text)', fontSize:12 }}>{line.account_name}</td>
                              <td style={{ padding:'6px 14px', color:'var(--textMuted)', fontSize:12 }}>{line.description || '—'}</td>
                              <td style={{ padding:'6px 14px', textAlign:'right', color:line.debit>0?blue:'var(--textFaint)', fontWeight:line.debit>0?600:400 }}>{line.debit > 0 ? fmtKES(line.debit) : '—'}</td>
                              <td style={{ padding:'6px 14px', textAlign:'right', color:line.credit>0?gold:'var(--textFaint)', fontWeight:line.credit>0?600:400 }}>{line.credit > 0 ? fmtKES(line.credit) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Chart of Accounts Tab ────────────────────────────────────── */}
      {tab === 'coa' && (
        <div style={{ padding: '0 20px' }}>
          {ACC_TYPES.map(type => {
            const accs = accounts.filter(a => a.account_type === type);
            if (!accs.length) return null;
            return (
              <div key={type} style={{ marginBottom: 20 }}>
                <SectionHead title={type.toUpperCase()} />
                <Card>
                  {accs.map(a => (
                    <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 120px 140px 80px', gap: 12, padding: '8px 0', borderBottom: '1px solid #1e1e1e', alignItems: 'center', fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: gold }}>{a.code}</span>
                      <span style={{ color: 'var(--text)' }}>{a.name}</span>
                      <span style={{ color: 'var(--textMuted)' }}>{a.sub_type || '—'}</span>
                      <span style={{ color: a.balance >= 0 ? green : red, fontWeight: 600 }}>{fmtKES(a.balance || 0)}</span>
                      <span style={{ color: a.is_active ? green : '#555', fontSize: 11 }}>{a.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  ))}
                </Card>
              </div>
            );
          })}
          {accounts.length === 0 && <Empty text="No accounts. Create your Chart of Accounts to get started." />}
        </div>
      )}

      {/* ── Trial Balance Tab ────────────────────────────────────────── */}
      {tab === 'tb' && (
        <div style={{ padding: '0 20px' }}>
          {!tb ? <div style={{ textAlign:'center', color:'var(--textMuted)', padding:40 }}>Loading trial balance…</div> : (
            <>
              {/* Summary cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
                {[
                  ['Total Debits',  fmtKES(tb.total_debit),  blue,  '📊'],
                  ['Total Credits', fmtKES(tb.total_credit), gold,  '📈'],
                  ['Accounts',      tb.rows.length,           'var(--text)', '🏦'],
                  ['Status', Math.abs(tb.total_debit-tb.total_credit)<0.01?'Balanced':'Unbalanced',
                    Math.abs(tb.total_debit-tb.total_credit)<0.01?green:red, Math.abs(tb.total_debit-tb.total_credit)<0.01?'✅':'⚠️'],
                ].map(([l,v,c,icon])=>(
                  <div key={l} style={{ background:'var(--surface)',borderRadius:12,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.07)',borderTop:`3px solid ${c}` }}>
                    <div style={{ fontSize:16, marginBottom:6 }}>{icon}</div>
                    <div style={{ fontWeight:800, fontSize:18, color:c }}>{v}</div>
                    <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
              {/* Table */}
              <div style={{ background:'var(--surface)', borderRadius:12, overflow:'hidden', border:'1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'rgba(255,255,255,0.05)' }}>
                        {[['Code','left'],['Account Name','left'],['Type','left'],['Debit (KES)','right'],['Credit (KES)','right']].map(([h,align])=>(
                          <th key={h} style={{ padding:'10px 14px', textAlign:align, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--textMuted)', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tb.rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'8px 14px', fontFamily:'monospace', fontSize:12, color:gold, fontWeight:700 }}>{row.code}</td>
                          <td style={{ padding:'8px 14px', color:'var(--text)', fontWeight:500 }}>{row.name}</td>
                          <td style={{ padding:'8px 14px' }}>
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'rgba(255,255,255,0.07)', color:'var(--textMuted)', textTransform:'uppercase' }}>{row.account_type}</span>
                          </td>
                          <td style={{ padding:'8px 14px', textAlign:'right', color:row.debit>0?blue:'rgba(255,255,255,0.2)', fontWeight:row.debit>0?600:400 }}>{row.debit>0?fmtKES(row.debit):'—'}</td>
                          <td style={{ padding:'8px 14px', textAlign:'right', color:row.credit>0?gold:'rgba(255,255,255,0.2)', fontWeight:row.credit>0?600:400 }}>{row.credit>0?fmtKES(row.credit):'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'rgba(201,168,76,0.12)', borderTop:'2px solid rgba(201,168,76,0.3)' }}>
                        <td colSpan={3} style={{ padding:'10px 14px', color:gold, fontWeight:800, fontSize:13 }}>TOTALS</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', color:blue, fontWeight:800 }}>{fmtKES(tb.total_debit)}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', color:gold, fontWeight:800 }}>{fmtKES(tb.total_credit)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div style={{ padding:'10px 14px', textAlign:'center', background:Math.abs(tb.total_debit-tb.total_credit)<0.01?`${green}12`:`${red}12`,
                  color:Math.abs(tb.total_debit-tb.total_credit)<0.01?green:red, fontWeight:700, fontSize:13 }}>
                  {Math.abs(tb.total_debit-tb.total_credit)<0.01 ? '✓ Ledger is in balance — Debits equal Credits' : `⚠ Out of balance by ${fmtKES(Math.abs(tb.total_debit-tb.total_credit))}`}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── P&L Tab ──────────────────────────────────────────────────── */}
      {tab === 'pl' && (
        <div style={{ padding: '0 20px' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-end' }}>
            <Field label="From"><input type="date" value={plDates.date_from} onChange={e => setPLDates(p => ({ ...p, date_from: e.target.value }))} style={inputStyle} /></Field>
            <Field label="To"><input type="date" value={plDates.date_to} onChange={e => setPLDates(p => ({ ...p, date_to: e.target.value }))} style={inputStyle} /></Field>
            <button onClick={loadPL} style={{ ...btnPrimary, marginBottom: 1 }}>Generate P&L</button>
          </div>
          {pl && (
            <Card>
              <SectionHead title="REVENUE" />
              {pl.revenues.map(r => <Row key={r.code} label={`${r.code} – ${r.name}`} value={r.amount} indent />)}
              <Row label="Total Revenue" value={pl.total_revenue} accent />
              <SectionHead title="EXPENSES" />
              {pl.expenses.map(r => <Row key={r.code} label={`${r.code} – ${r.name}`} value={r.amount} indent />)}
              <Row label="Total Expenses" value={pl.total_expenses} accent />
              <div style={{ height: 16 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: `2px solid ${gold}` }}>
                <span style={{ fontWeight: 800, fontSize: 15, color: gold }}>NET PROFIT / LOSS</span>
                <span style={{ fontWeight: 800, fontSize: 17, color: pl.net_profit >= 0 ? green : red }}>{fmtKES(pl.net_profit)}</span>
              </div>
            </Card>
          )}
          {!pl && <Empty text="Select a date range and click Generate P&L" />}
        </div>
      )}

      {/* ── Balance Sheet Tab ────────────────────────────────────────── */}
      {tab === 'bs' && (
        <div style={{ padding: '0 20px' }}>
          {!bs ? <div style={{ textAlign: 'center', color: 'var(--textMuted)', padding: 40 }}>Loading balance sheet…</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <Card title="Assets">
                {bs.assets.map(a => <Row key={a.code} label={`${a.code} – ${a.name}`} value={a.balance} indent />)}
                <Row label="TOTAL ASSETS" value={bs.totals.assets} accent />
              </Card>
              <div>
                <Card title="Liabilities">
                  {bs.liabilities.map(a => <Row key={a.code} label={`${a.code} – ${a.name}`} value={a.balance} indent />)}
                  <Row label="TOTAL LIABILITIES" value={bs.totals.liabilities} accent />
                </Card>
                <div style={{ height: 16 }} />
                <Card title="Equity">
                  {bs.equity.map(a => <Row key={a.code} label={`${a.code} – ${a.name}`} value={a.balance} indent />)}
                  <Row label="TOTAL EQUITY" value={bs.totals.equity} accent />
                  <div style={{ borderTop: `2px solid ${gold}`, marginTop: 10, paddingTop: 10 }}>
                    <Row label="LIABILITIES + EQUITY" value={bs.totals.liabilities + bs.totals.equity} accent />
                    <div style={{ textAlign: 'center', marginTop: 8, color: Math.abs(bs.totals.assets - (bs.totals.liabilities + bs.totals.equity)) < 0.01 ? green : red, fontSize: 12, fontWeight: 700 }}>
                      {Math.abs(bs.totals.assets - (bs.totals.liabilities + bs.totals.equity)) < 0.01 ? '✓ Balance Sheet balances' : '⚠ Balance Sheet does not balance'}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}